import { type Answers, answerIds, isEmpty } from "./answers";
import type { Condition, Question, QuestionnaireRevisionContents, Section, When } from "./contents";

const holds = (condition: Condition, value: Answers[string]["value"]): boolean => {
    if (!("question" in condition)) return true;
    if (isEmpty(value)) return false;
    const ids = answerIds(value);
    const expected = condition.value;
    switch (condition.op) {
        case "is":
            return ids.includes(String(expected));
        case "isNot":
            return !ids.includes(String(expected));
        case "anyOf":
            return (Array.isArray(expected) ? expected : [String(expected)]).some((id) => ids.includes(id));
        case "lte":
            return Number(value) <= Number(expected);
        case "gte":
            return Number(value) >= Number(expected);
    }
};

const evaluator = (contents: QuestionnaireRevisionContents, answers: Answers) => {
    const memo = new Map<string, boolean>();
    const visiting = new Set<string>();

    const met = (when: When | undefined): boolean => {
        if (!when) return true;
        const results = when.conditions.map((condition) => {
            if (!("question" in condition)) return holds(condition, undefined);
            return questionVisible(condition.question) && holds(condition, answers[condition.question]?.value);
        });
        return when.match === "all" ? results.every(Boolean) : results.some(Boolean);
    };

    const sectionVisible = (id: string): boolean => {
        const key = `s:${id}`;
        const cached = memo.get(key);
        if (cached !== undefined) return cached;
        if (visiting.has(key)) return false;
        visiting.add(key);
        const section = contents.sections.find((s) => s.id === id);
        const visible = section !== undefined && met(section.when);
        visiting.delete(key);
        memo.set(key, visible);
        return visible;
    };

    const questionVisible = (id: string): boolean => {
        const key = `q:${id}`;
        const cached = memo.get(key);
        if (cached !== undefined) return cached;
        if (visiting.has(key)) return false;
        visiting.add(key);
        const question = contents.questions.find((q) => q.id === id);
        const visible = question !== undefined && sectionVisible(question.section) && met(question.when);
        visiting.delete(key);
        memo.set(key, visible);
        return visible;
    };

    return { sectionVisible, questionVisible };
};

export const visibleSections = (contents: QuestionnaireRevisionContents, answers: Answers): Section[] => {
    const { sectionVisible } = evaluator(contents, answers);
    return contents.sections.filter((section) => sectionVisible(section.id));
};

export const visibleQuestions = (contents: QuestionnaireRevisionContents, answers: Answers): Question[] => {
    const { questionVisible } = evaluator(contents, answers);
    return contents.sections.flatMap((section) =>
        contents.questions.filter((q) => q.section === section.id && questionVisible(q.id))
    );
};
